import { Chip } from "@mui/material";
import { Box, Paper, Typography } from "@mui/material";
import { ReactNode } from "react";
import { KboPhaseRecord } from "../KboFlowDebug/KboFlowRunDebug";
import { formatNumber } from "../KboFlowDebug/KboFlowDebugUI";
import {
    DigestQuestion,
    formatPercent,
    questionStatus,
} from "./KboFlowRunDigestModels";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";

/**
 * A phase's own recorded status. Renders nothing when the run output did not
 * persist one, rather than implying success.
 */
export function PhaseStatusChip({ phase }: { phase?: KboPhaseRecord }) {
    const status = phase?.status?.trim();
    if (!status) return null;
    const normalized = status.toLowerCase();
    const color = normalized.includes("fail")
        ? ("error" as const)
        : normalized.includes("complet") || normalized.includes("succe")
          ? ("success" as const)
          : ("default" as const);
    return <Chip size="small" color={color} label={normalized} />;
}

export function StepNumber({
    number,
    color = pageColors.ink,
}: {
    number: number;
    color?: string;
}) {
    return (
        <Box
            sx={{
                alignItems: "center",
                bgcolor: color,
                borderRadius: "50%",
                color: pageColors.white,
                display: "inline-flex",
                flex: "0 0 auto",
                fontSize: 13,
                fontWeight: 800,
                height: 28,
                justifyContent: "center",
                width: 28,
            }}
        >
            {number}
        </Box>
    );
}

export function DigestCard({
    id,
    number,
    title,
    meta,
    color,
    children,
    sx,
}: {
    id?: string;
    number?: number;
    title: string;
    meta?: ReactNode;
    color?: string;
    children: ReactNode;
    sx?: object;
}) {
    return (
        <Paper
            id={id}
            variant="outlined"
            sx={{
                borderColor: "divider",
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                scrollMarginTop: 16,
                ...sx,
            }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.25,
                    mb: 2,
                }}
            >
                {number !== undefined && (
                    <StepNumber number={number} color={color} />
                )}
                <Typography
                    component="h2"
                    sx={{
                        color: pageColors.ink,
                        fontSize: 19,
                        fontWeight: 750,
                    }}
                >
                    {title}
                </Typography>
                {meta}
            </Box>
            {children}
        </Paper>
    );
}

export function StatusPill({
    status,
}: {
    status: ReturnType<typeof questionStatus>;
}) {
    const styles = {
        delivered: { bgcolor: pageColors.blueSoft, color: pageColors.blue },
        pursued: { bgcolor: pageColors.blueSoft, color: pageColors.blue },
        held: { bgcolor: "#F2F4F7", color: "#667085" },
        rejected: { bgcolor: "#FEF3F2", color: "#B42318" },
    }[status];
    return (
        <Box
            component="span"
            sx={{
                ...styles,
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 800,
                px: 0.75,
                py: 0.25,
                textTransform: "uppercase",
            }}
        >
            {status}
        </Box>
    );
}

export function GroundingChip({
    signal,
    emphasis,
}: {
    signal: NonNullable<DigestQuestion["groundingSignals"]>[number];
    emphasis?: boolean;
}) {
    const kindLabel =
        {
            topic: "topic",
            journeyStage: "journey stage",
            classifier: "classifier",
            modelResult: "model result",
            unknown: "program event",
        }[signal.kind ?? ""] ?? "signal";
    const parts = [
        kindLabel,
        signal.name,
        typeof signal.volume === "number"
            ? formatNumber(signal.volume)
            : undefined,
        typeof signal.eddyRate === "number"
            ? `eddy ${formatPercent(signal.eddyRate)}`
            : undefined,
    ].filter(Boolean);
    return (
        <Box
            component="span"
            sx={{
                bgcolor: emphasis ? pageColors.blueSoft : "#F2F4F7",
                borderRadius: 1.5,
                color: emphasis ? pageColors.blue : pageColors.muted,
                display: "inline-block",
                fontSize: 13,
                fontWeight: 700,
                px: 1.25,
                py: 0.6,
            }}
        >
            {parts.join(" · ")}
        </Box>
    );
}

export function Stat({
    value,
    label,
    accent,
}: {
    value: ReactNode;
    label: string;
    accent?: boolean;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                bgcolor: accent ? pageColors.blueSoft : pageColors.white,
                borderColor: accent ? pageColors.blue : "divider",
                borderRadius: 1.5,
                minWidth: 120,
                p: 1.75,
            }}
        >
            <Typography
                sx={{
                    color: accent ? pageColors.blue : pageColors.ink,
                    fontSize: 22,
                    fontWeight: 800,
                }}
            >
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Paper>
    );
}
